import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";
import { IoShareOutline } from "react-icons/io5";

export default function Sessions({ user }) {
  const [sessions, setSessions] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [sentInvitations, setSentInvitations] = useState([]); // Invitations envoyées
  const [isLoading, setIsLoading] = useState(true);
  const [newSessionName, setNewSessionName] = useState("");
  const [viewMode, setViewMode] = useState("list"); // "grid" or "list"
  const [sortBy, setSortBy] = useState("lastEdited"); // "date", "name", "dancers", "periods", "lastEdited"
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [shareModal, setShareModal] = useState(null); // Session ID pour le modal de partage
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [inviteSuccess, setInviteSuccess] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);
  const navigate = useNavigate();
  const apiUrl = `${process.env.REACT_APP_API_URL}/sessions`;
  const invitationsUrl = `${process.env.REACT_APP_API_URL}/invitations`;

  const [hoverDelete, setHoverDelete] = useState(null);

  useEffect(() => {
    AOS.init({ duration: 1200, once: true });
  }, []);

  // Fonction de chargement des sessions
  const fetchSessions = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${user.access_token}` },
      });
      setSessions(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Erreur lors du chargement des sessions :", error);
      setSessions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction de chargement des invitations reçues
  const fetchInvitations = async () => {
    try {
      const response = await axios.get(`${invitationsUrl}/received`, {
        headers: { Authorization: `Bearer ${user.access_token}` },
      });
      setInvitations(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des invitations :", error);
      setInvitations([]);
    }
  };

  // Fonction de chargement des invitations envoyées
  const fetchSentInvitations = async () => {
    try {
      const response = await axios.get(`${invitationsUrl}/sent`, {
        headers: { Authorization: `Bearer ${user.access_token}` },
      });
      setSentInvitations(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des invitations envoyées :", error);
      setSentInvitations([]);
    }
  };

  // Charger les données au montage uniquement
  useEffect(() => {
    if (user?.access_token) {
      fetchSessions();
      fetchInvitations();
      fetchSentInvitations();
    }
  }, [user?.access_token, apiUrl, invitationsUrl]);

  // Fonction pour rafraîchir manuellement
  const handleManualRefresh = () => {
    fetchSessions();
    fetchInvitations();
    fetchSentInvitations();
  };

  // Calculer le temps écoulé depuis le dernier refresh
  const getTimeSinceRefresh = () => {
    if (!lastRefresh) return "";
    const now = new Date();
    const diffMs = now - lastRefresh;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    
    if (diffSec < 60) return "just now";
    if (diffMin < 60) return `${diffMin} min ago`;
    return `${Math.floor(diffMin / 60)}h ago`;
  };

  const handleSessionClick = (sessionId) => {
    navigate(`/sessions/${sessionId}`);
  };

  // Envoyer une invitation
  const handleSendInvitation = async () => {
    if (!inviteEmail.trim() || !shareModal) return;
    
    setInviteError("");
    setInviteSuccess("");
    
    try {
      await axios.post(
        invitationsUrl,
        { session_id: shareModal, invitee_email: inviteEmail },
        { headers: { Authorization: `Bearer ${user.access_token}` } }
      );
      setInviteSuccess("Invitation envoyée !");
      setInviteEmail("");
      fetchSentInvitations(); // Rafraîchir la liste des invitations envoyées
      setTimeout(() => setInviteSuccess(""), 3000);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        setInviteError(detail);
      } else if (Array.isArray(detail)) {
        setInviteError(detail.map(d => d.msg || d).join(', '));
      } else if (detail?.msg) {
        setInviteError(detail.msg);
      } else {
        setInviteError("Erreur lors de l'envoi de l'invitation");
      }
    }
  };

  // Accepter une invitation
  const handleAcceptInvitation = async (invitationId) => {
    try {
      const response = await axios.post(
        `${invitationsUrl}/${invitationId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${user.access_token}` } }
      );
      
      // Rafraîchir les sessions et supprimer l'invitation de la liste
      const sessionsResponse = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${user.access_token}` },
      });
      setSessions(sessionsResponse.data);
      setInvitations(invitations.filter(inv => inv.id !== invitationId));
    } catch (error) {
      console.error("Erreur lors de l'acceptation de l'invitation :", error);
    }
  };

  // Refuser une invitation
  const handleDeclineInvitation = async (invitationId) => {
    try {
      await axios.post(
        `${invitationsUrl}/${invitationId}/decline`,
        {},
        { headers: { Authorization: `Bearer ${user.access_token}` } }
      );
      setInvitations(invitations.filter(inv => inv.id !== invitationId));
    } catch (error) {
      console.error("Erreur lors du refus de l'invitation :", error);
    }
  };

  // Annuler une invitation envoyée
  const handleCancelInvitation = async (invitationId) => {
    try {
      await axios.delete(
        `${invitationsUrl}/${invitationId}`,
        { headers: { Authorization: `Bearer ${user.access_token}` } }
      );
      setSentInvitations(sentInvitations.filter(inv => inv.id !== invitationId));
    } catch (error) {
      console.error("Erreur lors de l'annulation de l'invitation :", error);
    }
  };

  const handleCreateSession = async () => {
    if (!newSessionName.trim()) return;

    try {
      const response = await axios.post(
        apiUrl,
        { name: newSessionName },
        { headers: { Authorization: `Bearer ${user.access_token}` } }
      );

      setSessions([...sessions, response.data]);
      setNewSessionName("");
    } catch (error) {
      console.error("Erreur lors de la création de la session :", error);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await axios.delete(`${apiUrl}/${sessionId}`, {
        headers: { Authorization: `Bearer ${user.access_token}` },
      });

      setSessions(sessions.filter((session) => session.id !== sessionId));
      setDeleteConfirm(null); // Fermer la modale
    } catch (error) {
      console.error("Erreur lors de la suppression de la session :", error);
      setDeleteConfirm(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleCreateSession();
    }
  };

  // Helper pour normaliser les dates UTC
  const parseUTCDate = (dateString) => {
    if (!dateString) return new Date(0);
    let normalized = dateString;
    if (!dateString.endsWith('Z') && !dateString.includes('+')) {
      normalized = dateString + 'Z';
    }
    return new Date(normalized);
  };

  // Fonction de tri des sessions
  const getSortedSessions = () => {
    if (!sessions || sessions.length === 0) return [];
    
    const sessionsCopy = [...sessions];
    
    switch (sortBy) {
      case "name":
        return sessionsCopy.sort((a, b) => a.name.localeCompare(b.name));
      case "dancers":
        return sessionsCopy.sort((a, b) => (b.dancers?.length || 0) - (a.dancers?.length || 0));
      case "periods":
        return sessionsCopy.sort((a, b) => (b.periods?.length || 0) - (a.periods?.length || 0));
      case "date":
        return sessionsCopy.sort((a, b) => parseUTCDate(b.created_at) - parseUTCDate(a.created_at));
      case "lastEdited":
        // Newest first: sessions sans last_edited vont à la fin
        return sessionsCopy.sort((a, b) => {
          const aHasEdited = !!a.last_edited;
          const bHasEdited = !!b.last_edited;
          // Si l'un a last_edited et l'autre non, celui qui a last_edited vient en premier
          if (aHasEdited && !bHasEdited) return -1;
          if (!aHasEdited && bHasEdited) return 1;
          // Sinon, trier par date (last_edited si disponible, sinon created_at)
          return parseUTCDate(b.last_edited || b.created_at) - parseUTCDate(a.last_edited || a.created_at);
        });
      case "lastEditedOldest":
        // Oldest first: sessions sans last_edited vont au début
        return sessionsCopy.sort((a, b) => {
          const aHasEdited = !!a.last_edited;
          const bHasEdited = !!b.last_edited;
          // Si l'un a last_edited et l'autre non, celui qui n'a PAS last_edited vient en premier
          if (!aHasEdited && bHasEdited) return -1;
          if (aHasEdited && !bHasEdited) return 1;
          // Sinon, trier par date (oldest first)
          return parseUTCDate(a.last_edited || a.created_at) - parseUTCDate(b.last_edited || b.created_at);
        });
      default:
        return sessionsCopy.sort((a, b) => {
          const aHasEdited = !!a.last_edited;
          const bHasEdited = !!b.last_edited;
          if (aHasEdited && !bHasEdited) return -1;
          if (!aHasEdited && bHasEdited) return 1;
          return parseUTCDate(b.last_edited || b.created_at) - parseUTCDate(a.last_edited || a.created_at);
        });
    }
  };

  // Fonction pour formater la date de manière relative
  const getRelativeDate = (dateString, prefix = "Created") => {
    if (!dateString) return "";
    // Ajouter 'Z' si pas présent pour indiquer que c'est UTC
    let normalizedDateString = dateString;
    if (!dateString.endsWith('Z') && !dateString.includes('+')) {
      normalizedDateString = dateString + 'Z';
    }
    const date = new Date(normalizedDateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) return `${prefix} just now`;
    if (diffMinutes < 60) return `${prefix} ${diffMinutes} min ago`;
    if (diffHours < 24) return `${prefix} ${diffHours}h ago`;
    if (diffDays === 1) return `${prefix} yesterday`;
    if (diffDays < 7) return `${prefix} ${diffDays} days ago`;
    if (diffDays < 30) return `${prefix} ${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${prefix} ${Math.floor(diffDays / 30)} months ago`;
    return `${prefix} ${Math.floor(diffDays / 365)} years ago`;
  };

  const sortedSessions = getSortedSessions();

  return (
    <div style={pageStyle}>
      {/* Modern Navigation */}
      <nav style={navStyle}>
        <div style={navContainerStyle}>
          <span style={navLogoStyle} onClick={() => navigate("/")} role="button">
            Dansy
          </span>
          
          <div style={navButtonsStyle}>
            <button onClick={() => navigate("/profile")} style={profileButton}>
              <span style={{ marginRight: '8px' }}>👤</span>
              Profile
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div style={heroStyle}>
        <div style={heroContentStyle}>
          <h1 style={heroTitleStyle} data-aos="fade-down">My Sessions</h1>
          <p style={heroSubtitleStyle} data-aos="fade-up">
            {user?.name ? `Welcome back, ${user.name}!` : 'Manage your choreography sessions'}
          </p>
        </div>
      </div>
  
      {/* Content */}
      <div style={contentContainerStyle}>
        <div style={contentWrapperStyle}>
          {/* View Mode Selector and Sort */}
          <div style={viewSelectorContainerStyle}>
            {/* Sort Dropdown */}
            <div style={sortContainerStyle}>
              <label style={sortLabelStyle}>Sort by:</label>
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                style={sortSelectStyle}
              >
                <option value="lastEdited">Last edited (newest)</option>
                <option value="lastEditedOldest">Last edited (oldest)</option>
                <option value="date">Created (newest)</option>
                <option value="name">Name (A-Z)</option>
                <option value="dancers">Dancers (most)</option>
                <option value="periods">Periods (most)</option>
              </select>
            </div>

            {/* View Selector */}
            <div style={viewSelectorStyle}>
              <button
                onClick={() => setViewMode("grid")}
                style={{
                  ...viewButtonStyle,
                  ...(viewMode === "grid" ? viewButtonActiveStyle : {}),
                }}
              >
                <span style={{ fontSize: "18px" }}>⊞</span>
                <span>Grid</span>
              </button>
              <button
                onClick={() => setViewMode("list")}
                style={{
                  ...viewButtonStyle,
                  ...(viewMode === "list" ? viewButtonActiveStyle : {}),
                }}
              >
                <span style={{ fontSize: "18px" }}>☰</span>
                <span>List</span>
              </button>
            </div>
          </div>

          {/* Invitations reçues */}
          {invitations.length > 0 && (
            <div style={invitationsSectionStyle} data-aos="fade-up">
              <h3 style={invitationsTitleStyle}>
                📬 Invitations reçues ({invitations.length})
              </h3>
              <div style={invitationsListStyle}>
                {invitations.map((invitation) => (
                  <div key={invitation.id} style={invitationCardStyle}>
                    <div style={invitationInfoStyle}>
                      <span style={invitationSessionNameStyle}>
                        🎬 {invitation.session?.name || "Session"}
                      </span>
                      <span style={invitationFromStyle}>
                        de {invitation.inviter?.name || invitation.inviter?.email}
                      </span>
                    </div>
                    <div style={invitationActionsStyle}>
                      <button
                        onClick={() => handleAcceptInvitation(invitation.id)}
                        style={acceptButtonStyle}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#2ecc71";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#34c759";
                        }}
                      >
                        ✓ Accepter
                      </button>
                      <button
                        onClick={() => handleDeclineInvitation(invitation.id)}
                        style={declineButtonStyle}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#e74c3c";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#ff6b6b";
                        }}
                      >
                        ✕ Refuser
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sessions Display */}
          {isLoading ? (
            <div style={viewMode === "grid" ? sessionsGridStyle : sessionsListContainerStyle}>
              {[...Array(6)].map((_, index) => (
                <div key={index} style={viewMode === "grid" ? sessionSkeletonStyle : listSkeletonStyle} data-aos="fade-up" data-aos-delay={index * 50}>
                  <div style={skeletonHeaderStyle}></div>
                  <div style={skeletonLineStyle}></div>
                  <div style={skeletonLineStyle}></div>
                </div>
              ))}
            </div>
          ) : sortedSessions.length === 0 ? (
            <div style={emptyStateStyle} data-aos="fade-up">
              <div style={emptyIconStyle}>🎭</div>
              <h3 style={emptyTitleStyle}>No sessions yet</h3>
              <p style={emptyMessageStyle}>Create your first session below to get started</p>
            </div>
          ) : viewMode === "grid" ? (
            /* Grid View */
            <div style={sessionsGridStyle}>
              {sortedSessions.map((session, index) => (
                <div
                  key={session.id}
                  style={sessionCardStyle}
                  onClick={() => handleSessionClick(session.id)}
                  data-aos="fade-up"
                  data-aos-delay={index * 50}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 12px 32px rgba(255, 71, 126, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.08)";
                  }}
                >
                  {/* Bouton suppression en haut à droite */}
                  <div style={cardActionsStyle}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm(session.id);
                      }}
                      style={deleteButtonStyle}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#ff4d4d";
                        e.currentTarget.style.color = "white";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color = "#999";
                      }}
                    >
                      ×
                    </button>
                  </div>
                  
                  <div style={sessionIconStyle}>🎬</div>
                  <div style={sessionNameRowStyle}>
                    <h3 style={sessionNameStyle}>{session.name}</h3>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShareModal(session.id);
                        setInviteEmail("");
                        setInviteError("");
                        setInviteSuccess("");
                      }}
                      style={shareIconButtonStyle}
                      title="Partager"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "#ff477e";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = "#999";
                      }}
                    >
                      <IoShareOutline size={18} />
                    </button>
                  </div>
                  
                  <div style={sessionStatsStyle}>
                    <div style={statItemStyle}>
                      <span style={statIconStyle}>💃</span>
                      <span style={statValueStyle}>{session.dancers?.length || 0}</span>
                      <span style={statLabelStyle}>Dancers</span>
                    </div>
                    <div style={statDividerStyle}></div>
                    <div style={statItemStyle}>
                      <span style={statIconStyle}>📅</span>
                      <span style={statValueStyle}>{session.periods?.length || 0}</span>
                      <span style={statLabelStyle}>Periods</span>
                    </div>
                  </div>
                  
                  <div style={sessionFooterStyle}>
                    <span style={sessionDateStyle}>
                      {sortBy === "date" 
                        ? getRelativeDate(session.created_at, "Created")
                        : getRelativeDate(session.last_edited || session.created_at, "Edited")}
                    </span>
                    {session.users?.length > 1 && (
                      <span style={footerMembersBadgeStyle}>
                        👥 {session.users.length}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* List View */
            <div style={sessionsListContainerStyle}>
              {sortedSessions.map((session, index) => (
                <div
                  key={session.id}
                  style={sessionListItemStyle}
                  onClick={() => handleSessionClick(session.id)}
                  data-aos="fade-up"
                  data-aos-delay={index * 30}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#fafafa";
                    e.currentTarget.style.borderColor = "#ff477e";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "white";
                    e.currentTarget.style.borderColor = "#f0f0f0";
                  }}
                >
                  <div style={listLeftSectionStyle}>
                    <div style={listIconStyle}>🎬</div>
                    <div style={listInfoStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <h3 style={listSessionNameStyle}>{session.name}</h3>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShareModal(session.id);
                            setInviteEmail("");
                            setInviteError("");
                            setInviteSuccess("");
                          }}
                          style={listShareIconButtonStyle}
                          title="Partager"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = "#ff477e";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = "#999";
                          }}
                        >
                          <IoShareOutline size={16} />
                        </button>
                      </div>
                      <div style={listSessionMetaStyle}>
                        <span style={listSessionDateStyle}>
                          {sortBy === "date" 
                            ? getRelativeDate(session.created_at, "Created")
                            : getRelativeDate(session.last_edited || session.created_at, "Edited")}
                        </span>
                        {session.users?.length > 1 && (
                          <span style={listMembersBadgeStyle}>👥 {session.users.length}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div style={listRightSectionStyle}>
                    <div style={listStatsStyle}>
                      <div style={listStatItemStyle}>
                        <span style={listStatIconStyle}>💃</span>
                        <span style={listStatValueStyle}>{session.dancers?.length || 0}</span>
                      </div>
                      <div style={listStatItemStyle}>
                        <span style={listStatIconStyle}>📅</span>
                        <span style={listStatValueStyle}>{session.periods?.length || 0}</span>
                      </div>
                    </div>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm(session.id);
                      }}
                      style={listDeleteButtonStyle}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#ffe5e5";
                        e.currentTarget.style.color = "#ff4d4d";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color = "#999";
                      }}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Refresh Info */}
          {lastRefresh && (
            <div style={refreshInfoStyle}>
              <span style={refreshTextStyle}>Updated {getTimeSinceRefresh()}</span>
              <button 
                onClick={handleManualRefresh}
                style={refreshButtonStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#ff477e";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#999";
                }}
              >
                ↻ Refresh
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete/Leave Confirmation Modal */}
      {deleteConfirm && (
        <div style={modalOverlayStyle} onClick={() => setDeleteConfirm(null)}>
          <div style={modalContentStyle} onClick={(e) => e.stopPropagation()} data-aos="zoom-in">
            {sessions.find(s => s.id === deleteConfirm)?.users?.length > 1 ? (
              <>
                <div style={modalIconStyle}>👋</div>
                <h3 style={modalTitleStyle}>Leave Session?</h3>
                <p style={modalMessageStyle}>
                  Are you sure you want to leave "{sessions.find(s => s.id === deleteConfirm)?.name}"?
                  <br />
                  <span style={{ color: "#666", fontSize: "14px" }}>Other members will still have access to this session.</span>
                </p>
              </>
            ) : (
              <>
                <div style={modalIconStyle}>⚠️</div>
                <h3 style={modalTitleStyle}>Delete Session?</h3>
                <p style={modalMessageStyle}>
                  Are you sure you want to delete "{sessions.find(s => s.id === deleteConfirm)?.name}"?
                  <br />
                  <span style={modalWarningStyle}>This action cannot be undone.</span>
                </p>
              </>
            )}
            <div style={modalButtonsStyle}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={modalCancelButtonStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f0f0";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "white";
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteSession(deleteConfirm)}
                style={sessions.find(s => s.id === deleteConfirm)?.users?.length > 1 ? modalLeaveButtonStyle : modalDeleteButtonStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = sessions.find(s => s.id === deleteConfirm)?.users?.length > 1 ? "#e67e22" : "#e63946";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = sessions.find(s => s.id === deleteConfirm)?.users?.length > 1 ? "#f39c12" : "#ff4d4d";
                }}
              >
                {sessions.find(s => s.id === deleteConfirm)?.users?.length > 1 ? "Leave" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {shareModal && (
        <div style={modalOverlayStyle} onClick={() => setShareModal(null)}>
          <div style={shareModalContentStyle} onClick={(e) => e.stopPropagation()} data-aos="zoom-in">
            <div style={modalIconStyle}>🔗</div>
            <h3 style={modalTitleStyle}>Partager la session</h3>
            <p style={shareModalSubtitleStyle}>
              {sessions.find(s => s.id === shareModal)?.name}
            </p>
            
            <div style={inviteFormStyle}>
              <input
                type="email"
                placeholder="Email de l'invité..."
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendInvitation()}
                style={inviteInputStyle}
              />
              <button
                onClick={handleSendInvitation}
                style={inviteSendButtonStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#e63e7a";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#ff477e";
                }}
              >
                Envoyer
              </button>
            </div>
            
            {inviteError && <p style={inviteErrorStyle}>{inviteError}</p>}
            {inviteSuccess && <p style={inviteSuccessStyle}>{inviteSuccess}</p>}
            
            {/* Invitations en attente pour cette session */}
            {sentInvitations.filter(inv => inv.session_id === shareModal).length > 0 && (
              <div style={{ marginTop: "20px", textAlign: "left" }}>
                <p style={{ fontSize: "14px", color: "#666", marginBottom: "12px", fontWeight: "500" }}>
                  Invitations en attente :
                </p>
                {sentInvitations
                  .filter(inv => inv.session_id === shareModal)
                  .map(inv => (
                    <div 
                      key={inv.id} 
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px 14px",
                        backgroundColor: "#f8f8f8",
                        borderRadius: "10px",
                        marginBottom: "8px",
                      }}
                    >
                      <span style={{ fontSize: "14px", color: "#333" }}>
                        {inv.invitee_email}
                      </span>
                      <button
                        onClick={() => handleCancelInvitation(inv.id)}
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "transparent",
                          border: "1px solid #ff4d4d",
                          borderRadius: "8px",
                          color: "#ff4d4d",
                          fontSize: "12px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#ff4d4d";
                          e.currentTarget.style.color = "white";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.color = "#ff4d4d";
                        }}
                      >
                        Annuler
                      </button>
                    </div>
                  ))
                }
              </div>
            )}
            
            <button
              onClick={() => setShareModal(null)}
              style={closeModalButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f0f0f0";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Fermer
            </button>
          </div>
        </div>
      )}
  
      {/* Create Session Input */}
      <div style={createSessionContainerStyle}>
        <div style={createSessionWrapperStyle}>
          <input
            type="text"
            placeholder="Enter new session name..."
            value={newSessionName}
            onChange={(e) => setNewSessionName(e.target.value)}
            onKeyDown={handleKeyDown}
            style={inputStyle}
          />
          <button 
            onClick={handleCreateSession} 
            style={addButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.1)";
              e.currentTarget.style.boxShadow = "0 8px 16px rgba(255, 71, 126, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(255, 71, 126, 0.2)";
            }}
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
  
}

/* === Styles === */

/* === Styles === */
const pageStyle = {
  minHeight: "100vh",
  backgroundColor: "#fafafa",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
};

const navStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  backgroundColor: "rgba(255, 255, 255, 0.98)",
  borderBottom: "1px solid #f0f0f0",
  backdropFilter: "blur(10px)",
  zIndex: 1000,
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
};

const navContainerStyle = {
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "16px 24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const navLogoStyle = {
  fontSize: '1.5rem',
  fontFamily: "'Shirkhand', cursive",
  color: '#ff477e',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'transform 0.2s ease',
};

const navButtonsStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
};

const profileButton = {
  padding: "10px 20px",
  border: "1px solid #e0e0e0",
  borderRadius: "24px",
  backgroundColor: "white",
  color: "#333",
  fontSize: "15px",
  fontWeight: "500",
  cursor: "pointer",
  transition: "all 0.3s ease",
  display: "flex",
  alignItems: "center",
};

const heroStyle = {
  paddingTop: "120px",
  paddingBottom: "60px",
  background: "linear-gradient(135deg, #ff477e 0%, #ff6b9d 100%)",
  textAlign: "center",
  color: "white",
};

const heroContentStyle = {
  maxWidth: "800px",
  margin: "0 auto",
  padding: "0 24px",
};

const heroTitleStyle = {
  fontSize: "48px",
  fontWeight: "700",
  marginBottom: "16px",
  letterSpacing: "-0.02em",
};

const heroSubtitleStyle = {
  fontSize: "20px",
  opacity: 0.95,
  fontWeight: "400",
};

const contentContainerStyle = {
  maxWidth: "1200px",
  margin: "-40px auto 0",
  padding: "0 24px 120px",
  position: "relative",
};

const contentWrapperStyle = {
  backgroundColor: "white",
  borderRadius: "24px",
  padding: "40px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
};

const emptyStateStyle = {
  textAlign: "center",
  padding: "80px 20px",
};

const emptyIconStyle = {
  fontSize: "64px",
  marginBottom: "20px",
};

const emptyTitleStyle = {
  fontSize: "24px",
  fontWeight: "600",
  color: "#333",
  marginBottom: "12px",
};

const emptyMessageStyle = {
  fontSize: "16px",
  color: "#666",
};

const sessionsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
  gap: "24px",
};

const sessionCardStyle = {
  backgroundColor: "white",
  borderRadius: "16px",
  padding: "28px",
  cursor: "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  border: "1px solid #f0f0f0",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  position: "relative",
};

const deleteButtonStyle = {
  position: "absolute",
  top: "16px",
  right: "16px",
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  border: "none",
  backgroundColor: "transparent",
  color: "#999",
  fontSize: "24px",
  fontWeight: "300",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.2s ease",
  zIndex: 10,
};

const sessionIconStyle = {
  fontSize: "40px",
  marginBottom: "16px",
};

const sessionNameStyle = {
  fontSize: "28px",
  fontWeight: "600",
  color: "#1a1a1a",
  marginBottom: "20px",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  lineHeight: "1.4",
};

const sessionStatsStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-around",
  padding: "16px 0",
  borderTop: "1px solid #f0f0f0",
  borderBottom: "1px solid #f0f0f0",
  marginBottom: "16px",
};

const statItemStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "4px",
};

const statIconStyle = {
  fontSize: "20px",
};

const statValueStyle = {
  fontSize: "20px",
  fontWeight: "700",
  color: "#ff477e",
};

const statLabelStyle = {
  fontSize: "12px",
  color: "#999",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const statDividerStyle = {
  width: "1px",
  height: "40px",
  backgroundColor: "#f0f0f0",
};

const sessionFooterStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const sessionDateStyle = {
  fontSize: "14px",
  color: "#999",
};

const sessionSkeletonStyle = {
  backgroundColor: "#f5f5f5",
  borderRadius: "16px",
  padding: "28px",
  height: "220px",
  animation: "pulse 1.5s ease-in-out infinite",
};

const skeletonHeaderStyle = {
  width: "60%",
  height: "24px",
  backgroundColor: "#e0e0e0",
  borderRadius: "8px",
  marginBottom: "16px",
};

const skeletonLineStyle = {
  width: "100%",
  height: "16px",
  backgroundColor: "#e0e0e0",
  borderRadius: "8px",
  marginBottom: "12px",
};

const createSessionContainerStyle = {
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: "rgba(255, 255, 255, 0.98)",
  backdropFilter: "blur(10px)",
  padding: "20px 24px",
  boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
  zIndex: 999,
};

const createSessionWrapperStyle = {
  maxWidth: "600px",
  margin: "0 auto",
  display: "flex",
  alignItems: "center",
  gap: "12px",
};

const inputStyle = {
  flex: 1,
  padding: "16px 24px",
  fontSize: "16px",
  border: "2px solid #e0e0e0",
  borderRadius: "24px",
  outline: "none",
  transition: "all 0.3s ease",
  backgroundColor: "white",
};

const addButtonStyle = {
  width: "56px",
  height: "56px",
  borderRadius: "50%",
  border: "none",
  background: "linear-gradient(135deg, #ff477e, #ff6b9d)",
  color: "white",
  fontSize: "32px",
  fontWeight: "300",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: "0 4px 12px rgba(255, 71, 126, 0.2)",
};

// View Selector Styles
const viewSelectorContainerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "24px",
};

const sortContainerStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
};

const sortLabelStyle = {
  fontSize: "14px",
  fontWeight: "500",
  color: "#666",
};

const sortSelectStyle = {
  padding: "8px 16px",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  backgroundColor: "white",
  color: "#333",
  fontSize: "14px",
  fontWeight: "500",
  cursor: "pointer",
  outline: "none",
  transition: "border-color 0.2s ease",
};

const viewSelectorStyle = {
  display: "flex",
  gap: "8px",
  padding: "4px",
  backgroundColor: "#f5f5f5",
  borderRadius: "12px",
};

const viewButtonStyle = {
  padding: "8px 16px",
  border: "none",
  backgroundColor: "transparent",
  color: "#666",
  fontSize: "14px",
  fontWeight: "500",
  cursor: "pointer",
  borderRadius: "8px",
  transition: "all 0.2s ease",
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

const viewButtonActiveStyle = {
  backgroundColor: "white",
  color: "#ff477e",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
};

// List View Styles
const sessionsListContainerStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const sessionListItemStyle = {
  backgroundColor: "white",
  borderRadius: "12px",
  padding: "20px 24px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  border: "1px solid #f0f0f0",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const listLeftSectionStyle = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  flex: 1,
};

const listIconStyle = {
  fontSize: "32px",
};

const listInfoStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

const listSessionNameStyle = {
  fontSize: "18px",
  fontWeight: "600",
  color: "#1a1a1a",
  margin: 0,
};

const listSessionDateStyle = {
  fontSize: "13px",
  color: "#999",
};

const listRightSectionStyle = {
  display: "flex",
  alignItems: "center",
  gap: "24px",
};

const listStatsStyle = {
  display: "flex",
  gap: "20px",
};

const listStatItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

const listStatIconStyle = {
  fontSize: "16px",
};

const listStatValueStyle = {
  fontSize: "16px",
  fontWeight: "600",
  color: "#ff477e",
};

const listDeleteButtonStyle = {
  width: "36px",
  height: "36px",
  borderRadius: "50%",
  border: "none",
  backgroundColor: "transparent",
  color: "#999",
  fontSize: "24px",
  fontWeight: "300",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.2s ease",
};

const listSkeletonStyle = {
  backgroundColor: "#f5f5f5",
  borderRadius: "12px",
  padding: "20px 24px",
  height: "80px",
  animation: "pulse 1.5s ease-in-out infinite",
};

// Modal Styles
const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2000,
  backdropFilter: "blur(4px)",
};

const modalContentStyle = {
  backgroundColor: "white",
  borderRadius: "20px",
  padding: "40px",
  maxWidth: "450px",
  width: "90%",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
  textAlign: "center",
};

const modalIconStyle = {
  fontSize: "64px",
  marginBottom: "20px",
};

const modalTitleStyle = {
  fontSize: "24px",
  fontWeight: "700",
  color: "#1a1a1a",
  marginBottom: "12px",
};

const modalMessageStyle = {
  fontSize: "16px",
  color: "#666",
  marginBottom: "32px",
  lineHeight: "1.6",
};

const modalWarningStyle = {
  color: "#ff4d4d",
  fontWeight: "600",
  fontSize: "14px",
};

const modalButtonsStyle = {
  display: "flex",
  gap: "12px",
  justifyContent: "center",
};

const modalCancelButtonStyle = {
  padding: "12px 32px",
  border: "1px solid #e0e0e0",
  borderRadius: "12px",
  backgroundColor: "white",
  color: "#666",
  fontSize: "15px",
  fontWeight: "500",
  cursor: "pointer",
  transition: "all 0.2s ease",
};

const modalDeleteButtonStyle = {
  padding: "12px 32px",
  border: "none",
  borderRadius: "12px",
  backgroundColor: "#ff4d4d",
  color: "white",
  fontSize: "15px",
  fontWeight: "500",
  cursor: "pointer",
  transition: "all 0.2s ease",
};

const modalLeaveButtonStyle = {
  padding: "12px 32px",
  border: "none",
  borderRadius: "12px",
  backgroundColor: "#f39c12",
  color: "white",
  fontSize: "15px",
  fontWeight: "500",
  cursor: "pointer",
  transition: "all 0.2s ease",
};

// Styles pour le système d'invitations
const invitationsSectionStyle = {
  marginBottom: "48px",
  padding: "24px",
  backgroundColor: "#fff8e6",
  borderRadius: "16px",
  border: "1px solid #ffd700",
};

const invitationsSectionTitleStyle = {
  fontSize: "20px",
  fontWeight: "600",
  color: "#333",
  marginBottom: "16px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const invitationCardStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px",
  backgroundColor: "white",
  borderRadius: "12px",
  marginBottom: "12px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
};

const invitationCardContentStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

const invitationSessionNameStyle = {
  fontSize: "16px",
  fontWeight: "600",
  color: "#333",
};

const invitationFromStyle = {
  fontSize: "14px",
  color: "#666",
};

const invitationActionsStyle = {
  display: "flex",
  gap: "8px",
};

const invitationAcceptButtonStyle = {
  padding: "8px 16px",
  backgroundColor: "#4CAF50",
  color: "white",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: "500",
  transition: "all 0.2s ease",
};

const invitationDeclineButtonStyle = {
  padding: "8px 16px",
  backgroundColor: "#f0f0f0",
  color: "#666",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: "500",
  transition: "all 0.2s ease",
};

const shareButtonStyle = {
  padding: "6px 12px",
  backgroundColor: "#e8f4ff",
  color: "#2196F3",
  border: "1px solid #2196F3",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "18px",
  fontWeight: "500",
  display: "flex",
  alignItems: "center",
  gap: "4px",
  transition: "all 0.2s ease",
};

const membersInfoStyle = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
  fontSize: "13px",
  color: "#666",
  marginTop: "4px",
};

const shareModalInputStyle = {
  width: "100%",
  padding: "12px 16px",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  fontSize: "15px",
  marginBottom: "12px",
  outline: "none",
  transition: "border-color 0.2s ease",
};

const shareModalButtonStyle = {
  width: "100%",
  padding: "12px",
  backgroundColor: "#2196F3",
  color: "white",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "15px",
  fontWeight: "500",
  marginBottom: "12px",
  transition: "all 0.2s ease",
};

const shareModalCopyButtonStyle = {
  width: "100%",
  padding: "12px",
  backgroundColor: "#f5f5f5",
  color: "#666",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "500",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  transition: "all 0.2s ease",
};

const shareModalErrorStyle = {
  color: "#ff4d4d",
  fontSize: "14px",
  marginBottom: "12px",
  textAlign: "center",
};

const shareModalSuccessStyle = {
  color: "#4CAF50",
  fontSize: "14px",
  marginBottom: "12px",
  textAlign: "center",
};

const shareModalDividerStyle = {
  display: "flex",
  alignItems: "center",
  margin: "16px 0",
  gap: "12px",
};

const shareModalDividerLineStyle = {
  flex: 1,
  height: "1px",
  backgroundColor: "#e0e0e0",
};

const shareModalDividerTextStyle = {
  color: "#999",
  fontSize: "13px",
};

// Styles manquants pour les invitations
const invitationsTitleStyle = {
  fontSize: "20px",
  fontWeight: "600",
  color: "#333",
  marginBottom: "16px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const invitationsListStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const invitationInfoStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

const acceptButtonStyle = {
  padding: "8px 16px",
  backgroundColor: "#34c759",
  color: "white",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: "500",
  fontSize: "14px",
  transition: "all 0.2s ease",
};

const declineButtonStyle = {
  padding: "8px 16px",
  backgroundColor: "#ff6b6b",
  color: "white",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: "500",
  fontSize: "14px",
  transition: "all 0.2s ease",
};

const cardActionsStyle = {
  position: "absolute",
  top: "12px",
  right: "12px",
  display: "flex",
  gap: "4px",
};

const membersBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  padding: "4px 10px",
  backgroundColor: "#e8f4ff",
  color: "#2196F3",
  borderRadius: "12px",
  fontSize: "12px",
  fontWeight: "500",
  marginTop: "8px",
};

const listMembersBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  padding: "2px 8px",
  backgroundColor: "#e8f4ff",
  color: "#2196F3",
  borderRadius: "8px",
  fontSize: "11px",
  fontWeight: "500",
};

const listShareButtonStyle = {
  padding: "8px",
  backgroundColor: "transparent",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "16px",
  color: "#999",
  transition: "all 0.2s ease",
};

const shareModalContentStyle = {
  backgroundColor: "white",
  padding: "32px",
  borderRadius: "20px",
  textAlign: "center",
  maxWidth: "400px",
  width: "90%",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
};

const shareModalSubtitleStyle = {
  fontSize: "16px",
  color: "#666",
  marginBottom: "24px",
};

const inviteFormStyle = {
  display: "flex",
  gap: "8px",
  marginBottom: "16px",
};

const inviteInputStyle = {
  flex: 1,
  padding: "12px 16px",
  border: "1px solid #e0e0e0",
  borderRadius: "10px",
  fontSize: "15px",
  outline: "none",
  transition: "border-color 0.2s ease",
};

const inviteSendButtonStyle = {
  padding: "12px 20px",
  backgroundColor: "#ff477e",
  color: "white",
  border: "none",
  borderRadius: "10px",
  cursor: "pointer",
  fontSize: "15px",
  fontWeight: "500",
  transition: "all 0.2s ease",
  whiteSpace: "nowrap",
};

const inviteErrorStyle = {
  color: "#ff4d4d",
  fontSize: "14px",
  marginBottom: "12px",
};

const inviteSuccessStyle = {
  color: "#34c759",
  fontSize: "14px",
  marginBottom: "12px",
};

const closeModalButtonStyle = {
  padding: "12px 24px",
  backgroundColor: "transparent",
  color: "#666",
  border: "1px solid #e0e0e0",
  borderRadius: "10px",
  cursor: "pointer",
  fontSize: "15px",
  fontWeight: "500",
  transition: "all 0.2s ease",
  marginTop: "8px",
};

// Styles pour l'icône de partage Apple et le row du nom
const sessionNameRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  width: "100%",
};

const shareIconButtonStyle = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "#999",
  padding: "4px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "color 0.2s ease",
};

const listShareIconButtonStyle = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "#999",
  padding: "2px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "color 0.2s ease",
};

const footerMembersBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  padding: "2px 8px",
  backgroundColor: "#e8f4ff",
  color: "#2196F3",
  borderRadius: "8px",
  fontSize: "12px",
  fontWeight: "500",
};

const listSessionMetaStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
};

// Styles pour le message de refresh
const refreshInfoStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  padding: "16px 0",
  marginTop: "24px",
};

const refreshTextStyle = {
  fontSize: "13px",
  color: "#999",
};

const refreshButtonStyle = {
  background: "none",
  border: "none",
  cursor: "pointer",
  fontSize: "13px",
  color: "#999",
  fontWeight: "500",
  padding: "4px 8px",
  borderRadius: "8px",
  transition: "color 0.2s ease",
};

